'use client';

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { RateLimitError } from '@/lib/rate-limit-error';
import { openDeferredEventSource } from '@/lib/open-deferred-event-source';

/**
 * A single activity event from the global activity API.
 * Includes pool metadata and user identities.
 */
export interface GlobalActivityItem {
  id: string;
  eventType: string;
  poolId: string;
  seatIndex?: number;
  user: string;
  userIdentity?: { displayName?: string; avatarUrl?: string; level?: number };
  oldHolder?: string;
  oldHolderIdentity?: { displayName?: string; avatarUrl?: string; level?: number };
  newHolder?: string;
  newHolderIdentity?: { displayName?: string; avatarUrl?: string; level?: number };
  price?: string;
  newPrice?: string;
  amount?: string;
  timestamp: number;
  txHash: string;
  // Pool metadata
  coinSymbol?: string;
  coinImageUrl?: string;
  coinAddress?: string;
  coinName?: string;
  // Condensed fields
  seatCount?: number;
  seatIndices?: number[];
  totalPrice?: string;
  isTakeover?: boolean;
  isPreGraduation?: boolean;
  // V2 yield claim fields
  gridId?: string;
  yieldTokenAddress?: string;
  yieldTokenSymbol?: string;
  yieldTokenDecimals?: number;
  /** Soft-moderation flag stamped by `/api/activity/global` when the
   *  event's `gridId` is in `banned_grids`. The takeover-app ticker
   *  filters these out below; SDK builders see them with the flag
   *  set and decide their own UX. */
  banned?: boolean;
}

const QUERY_KEY = 'global-activity';

// Module-level store for synthetic items (e.g. claim confirmations) that must
// survive refetches until the indexer catches up (~60s TTL).
let _pinnedItems: GlobalActivityItem[] = [];

/**
 * Pin synthetic activity items so they persist across refetch cycles.
 * Call this instead of (or in addition to) writing to the query cache.
 */
export function pinGlobalActivityItems(items: GlobalActivityItem[]) {
  _pinnedItems = [...items, ..._pinnedItems];
}

interface UseGlobalActivityReturn {
  /** All activity items (newest first) */
  items: GlobalActivityItem[];
  /** Whether the initial fetch is loading */
  isLoading: boolean;
  /** Manually inject a test item (prepends to the list, triggers re-render) */
  injectTestItem: (item: GlobalActivityItem) => void;
}

/**
 * Hook that feeds the activity ticker. Previously a blind 15s poll of
 * `/api/activity/global`; now driven by the shared `/api/live` SSE
 * stream so the feed is near-instant when things happen and silent
 * when the network is idle.
 *
 * Wiring:
 *  1. `useQuery` does a single **prime** fetch against
 *     `/api/activity/global?limit=N` (fully enriched payload — coin
 *     metadata, identities, condensed seat counts). No
 *     `refetchInterval`, so we don't re-hit the endpoint on a timer.
 *  2. An `EventSource('/api/live')` subscribes to every boy-meets-board
 *     event across all pools. On each burst we schedule a debounced
 *     `queryClient.invalidateQueries` (250 ms window) so back-to-back
 *     events coalesce into one refetch for the enriched payload.
 *  3. A low-frequency safety net (`refetchInterval: 90_000`) kicks in
 *     only as a reconnect/desync guard — the SSE pipeline is the
 *     primary driver.
 *
 * Net effect: ~4 polls/min → ~0.7 polls/min at rest, with faster
 * perceived updates during activity.
 */
export function useGlobalActivity(
  limit: number = 15,
  options?: { enabled?: boolean },
): UseGlobalActivityReturn {
  const queryClient = useQueryClient();
  const enabled = options?.enabled !== false;

  const { data, isLoading } = useQuery<GlobalActivityItem[]>({
    queryKey: [QUERY_KEY, limit],
    queryFn: async () => {
      const res = await fetch(`/api/activity/global?limit=${limit}`);
      if (res.status === 429) throw new RateLimitError();
      if (!res.ok) return [];
      const json = await res.json();
      return (json.data || []) as GlobalActivityItem[];
    },
    staleTime: 12_000,
    // Long-interval safety net so a lost SSE connection eventually
    // recovers without user interaction. SSE invalidation is the
    // primary driver — see the effect below.
    refetchInterval: 90_000,
    refetchOnWindowFocus: false,
    enabled,
  });

  // Subscribe to /api/live and invalidate the enriched query on every
  // (debounced) burst of events. We don't bother parsing event data —
  // the activity endpoint already condenses/deduplicates, so the only
  // thing we care about is "something changed, refetch now".
  const invalidateTimerRef = useRef<number | null>(null);
  useEffect(() => {
    if (!enabled) return;
    if (typeof window === 'undefined') return;

    let closed = false;
    let reconnectTimer: number | null = null;
    let closeEs: (() => void) | null = null;

    const scheduleInvalidate = () => {
      if (invalidateTimerRef.current !== null) return;
      invalidateTimerRef.current = window.setTimeout(() => {
        invalidateTimerRef.current = null;
        queryClient.invalidateQueries({ queryKey: [QUERY_KEY, limit] });
      }, 250);
    };

    const handler = () => {
      if (!closed) scheduleInvalidate();
    };

    const connect = () => {
      if (closed) return;
      // `openDeferredEventSource` absorbs React StrictMode's dev-only
      // double-mount so we don't open → close → reopen on every
      // navigation during development.
      closeEs = openDeferredEventSource('/api/live', (es) => {
        // Match the event names emitted by /api/live::mapEventType().
        const names = [
          'seat_claimed',
          'seat_bought',
          'seat_abandoned',
          'seat_forfeited',
          'price_updated',
          'deposit_added',
          'balance_withdrawn',
          'creator_fees_withdrawn',
          'fees_accrued',
        ];
        for (const name of names) es.addEventListener(name, handler);

        es.onerror = () => {
          if (closed) return;
          closeEs?.();
          closeEs = null;
          // Back off before reconnecting so the browser doesn't hammer
          // the endpoint during a server restart. A single slow retry
          // is fine — the `refetchInterval` safety net still keeps the
          // UI fresh.
          reconnectTimer = window.setTimeout(connect, 5_000);
        };
      });
    };

    connect();

    return () => {
      closed = true;
      if (reconnectTimer !== null) window.clearTimeout(reconnectTimer);
      if (invalidateTimerRef.current !== null) {
        window.clearTimeout(invalidateTimerRef.current);
        invalidateTimerRef.current = null;
      }
      closeEs?.();
    };
  }, [enabled, limit, queryClient]);

  // Inject a test item by mutating the query cache directly (triggers re-render)
  const injectTestItem = useCallback(
    (item: GlobalActivityItem) => {
      queryClient.setQueryData<GlobalActivityItem[]>(
        [QUERY_KEY, limit],
        (prev) => [item, ...(prev || [])].slice(0, limit + 5)
      );
    },
    [queryClient, limit]
  );

  const items = useMemo(() => {
    // `/api/activity/global` stamps `banned: true` on events for
    // boards in `banned_grids` instead of dropping them, so SDK
    // builders can decide their own moderation UX. The in-app
    // ticker keeps the previous "hide" behaviour by filtering
    // here.
    const apiItems = (data || []).filter((i) => !i.banned);
    if (_pinnedItems.length === 0) return apiItems;

    const now = Math.floor(Date.now() / 1000);
    _pinnedItems = _pinnedItems.filter(i => now - i.timestamp < 60);
    if (_pinnedItems.length === 0) return apiItems;

    const apiIds = new Set(apiItems.map(i => i.id));
    const surviving = _pinnedItems.filter(i => !apiIds.has(i.id));
    return surviving.length > 0 ? [...surviving, ...apiItems] : apiItems;
  }, [data]);

  return {
    items,
    isLoading,
    injectTestItem,
  };
}
